import { ReactElement, ReactNode } from 'react';

import { Badge } from '@components/badge';
import { BadgeVariant } from '@components/badge/badge';
import { Card } from '@components/card';
import { IconProps } from '@components/icons';
import { IconPause } from '@components/icons/pause';
import { IconPlay } from '@components/icons/play';
import { IconRedo } from '@components/icons/redo';
import { IconRemove } from '@components/icons/remove';
import { IconRestart } from '@components/icons/restart';
import { IconTravellerStatusCompleted } from '@components/icons/traveller-status-completed';
import { IconTravellerStatusInProgress } from '@components/icons/traveller-status-in-progress';
import { IconTravellerStatusNotStarted } from '@components/icons/traveller-status-not-started';
import { IconTravellerStatusPaused } from '@components/icons/traveller-status-paused';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

export type StatisticsDataType = {
  total: number;
  notStarted: number;
  inProgress: number;
  paused: number;
  completed: number;
  toBeStarted: number;
  toBePaused: number;
  toBeResumed: number;
  toBeReset: number;
  toBeRemoved: number;
};

type BulkTravellerModalCountType = {
  type: 'start' | 'pause' | 'resume' | 'reset' | 'remove';
} & StatisticsDataType;

export const BulkTravellerModalCount = ({
  type,
  total,
  notStarted,
  inProgress,
  paused,
  completed,
  toBeStarted,
  toBePaused,
  toBeResumed,
  toBeReset,
  toBeRemoved,
}: BulkTravellerModalCountType) => {
  const { t } = useScopedTranslate(locale);

  const typeMap = {
    start: {
      icon: <IconPlay />,
      text: t('toBeStarted'),
      count: toBeStarted,
    },
    pause: {
      icon: <IconPause />,
      text: t('toBePaused'),
      count: toBePaused,
    },
    resume: {
      icon: <IconRedo />,
      text: t('toBeResumed'),
      count: toBeResumed,
    },
    reset: {
      icon: <IconRestart />,
      text: t('toBeReset'),
      count: toBeReset,
    },
    remove: {
      icon: <IconRemove />,
      text: t('toBeRemoved'),
      count: toBeRemoved,
    },
  };
  const typeInfo = typeMap[type];

  return (
    <Stack space={4}>
      <Stack orientation="horizontal" justify="space-between">
        <Text font="bold16" text={t('travellersSelected')} />
        <Badge text={total} size="md" />
      </Stack>
      <Separator />
      <Stack space={1}>
        <TypeCount
          icon={<IconTravellerStatusNotStarted />}
          description={t('notStarted')}
          children={notStarted}
          variant={['start', 'remove'].includes(type) ? 'green' : 'red'}
        />
        <TypeCount
          icon={<IconTravellerStatusInProgress />}
          description={t('inProgress')}
          children={inProgress}
          variant={['pause', 'reset', 'remove'].includes(type) ? 'green' : 'red'}
        />
        <TypeCount
          icon={<IconTravellerStatusPaused />}
          description={t('paused')}
          children={paused}
          variant={['resume', 'reset', 'remove'].includes(type) ? 'green' : 'red'}
        />
        <TypeCount
          icon={<IconTravellerStatusCompleted />}
          description={t('completed')}
          children={completed}
          variant={['reset', 'remove'].includes(type) ? 'green' : 'red'}
        />
      </Stack>
      <Separator />
      <Card
        space={4}
        variant="default"
        style={{ backgroundColor: theme.color.blueTrans5, paddingRight: theme.space[6] }}
      >
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" space={2}>
            {typeInfo.icon}
            <Text font="bold16" text={typeInfo.text} />
          </Stack>
          <Text font="bold16" color="textSecondary" text={typeInfo.count} />
        </Stack>
      </Card>
    </Stack>
  );
};

const TypeCount = ({
  icon: Icon,
  description,
  variant,
  children,
}: {
  icon: ReactElement<IconProps>;
  description: string;
  variant: BadgeVariant;
  children: ReactNode;
}) => (
  <Stack orientation="horizontal" justify="space-between">
    <Stack orientation="horizontal" space={3}>
      {Icon}
      <Text font="bold16" text={description} />
    </Stack>
    <Badge text={String(children)} size="md" variant={variant} />
  </Stack>
);

const locale = {
  en: {
    resume: 'Resume',
    travellersSelected: 'Traveller/s Selected',
    notStarted: 'Not started',
    inProgress: 'In progress',
    paused: 'Paused',
    completed: 'Completed',
    toBeStarted: 'To be started',
    toBePaused: 'To be paused',
    toBeResumed: 'To be resumed',
    toBeReset: 'To be reset',
    toBeRemoved: 'To be removed',
  },
};
